/**
 * Mission Editor Constraints
 *
 * This file defines all the constraints, min/max values, and defaults for mission editing.
 * These values are shared between cloud web and mobile platforms to ensure consistent behavior.
 *
 * @remarks
 * All speed values are in meters per second (m/s).
 * All distance/height values are in meters (m).
 * All angles are in degrees unless otherwise specified.
 * All time values are in seconds (s).
 */

import { feetToMeters, mphToMps } from "@skydio/math";

/**
 * Vehicle types for constraint lookups
 */
export type VehicleType = string;

/**
 * Vehicle configuration interface (minimal)
 *
 * @remarks
 * This is a simplified version that supports both cloud and mobile platforms.
 * vehicleType can be either the internal code (R47, E1, R3) or public name (X10, X2, S2).
 */
export interface VehicleConfiguration {
  vehicleType: VehicleType;
  gimbalType?: string;
}

/**
 * Flight mode types
 */
export type FlightMode = "nominal" | "twilight";

/**
 * Waypoint speed constraints
 *
 * @remarks
 * These values represent the maximum and minimum transit speed between waypoints.
 * The value doesn't necessarily match the drone's physical capability - it may be
 * lower due to design decisions.
 *
 * Minimum value is the current minimum speed supported by the waypoints skill.
 */
export const WAYPOINT_SPEED = {
  MIN: 1.0, // m/s
  DEFAULT: 5.0, // m/s
  DEFAULT_INDOOR: 2.0, // m/s
  MAX: {
    S2_X2: {
      nominal: 10.0,
      twilight: 7.0,
    },
    R47_X10: {
      nominal: 16.0, // MAX_VISUAL_NAVIGATION_SPEED
      twilight: 14.0, // MAX_TWLIGHT_MODE_SPEED (corrected from cloud)
    },
  },
} as const;

/**
 * Return speed constraints
 *
 * @remarks
 * See vehicle/ambassador/params.py : return_settings_limits for detailed information
 */
export const RETURN_SPEED = {
  MIN: 2.0, // m/s
  DEFAULT: 2.0, // m/s (S2/X2)
  DEFAULT_R47: 5.0, // m/s (R47/X10)
  // Product-recommended return speed surfaced as a caret + warning marker in
  // Return Settings. Not a vehicle physics limit. See SW-93253.
  OPTIMAL: {
    R47_X10: mphToMps(26), // m/s
  },
  MAX: {
    S2_X2: {
      nominal: 10.0,
      twilight: 7.0,
    },
    R47_X10: {
      nominal: 16.0, // MAX_VISUAL_NAVIGATION_SPEED
      twilight: 14.0, // MAX_TWLIGHT_MODE_SPEED (corrected from cloud)
    },
  },
} as const;

/**
 * Waypoint height constraints (takeoff-relative frame)
 *
 * @remarks
 * Heights are relative to the takeoff point
 */
export const WAYPOINT_HEIGHT = {
  MIN: feetToMeters(-2500), // meters (~-762m)
  MAX: feetToMeters(5000), // meters (~1524m)
  DEFAULT: 30, // meters
} as const;

/**
 * Waypoint heading constraints
 *
 * @remarks
 * 0 degrees = True North (GPS frame) or vehicle facing direction (NAV frame)
 */
export const WAYPOINT_HEADING = {
  MIN: 0, // degrees
  MAX: 360, // degrees
  STEP: 1, // degrees
  DEFAULT: 0, // degrees
} as const;

/**
 * Waypoint gimbal pitch constraints
 *
 * @remarks
 * Negative numbers point towards the ground
 * Positive numbers point towards the sky
 */
export const WAYPOINT_GIMBAL_PITCH = {
  MIN: -90, // degrees
  MAX: 90, // degrees
  STEP: 1, // degrees
  DEFAULT: -45, // degrees
} as const;

/**
 * Waypoint pause time constraints (timed wait)
 *
 * @remarks
 * Time to pause at waypoint. In-transit actions continue during pause.
 */
export const WAYPOINT_PAUSE_TIME = {
  MIN: 0, // seconds
  MAX: 60, // seconds
  STEP: 1, // seconds
  DEFAULT: 0, // seconds
} as const;

/**
 * Map capture gimbal angle constraints (nadir)
 */
export const MAP_CAPTURE_GIMBAL = {
  MIN: -90, // degrees
  MAX: -55, // degrees
  STEP: 1, // degrees
  DEFAULT: -80, // degrees
} as const;

/**
 * Map capture diagonal gimbal pitch constraints
 */
export const MAP_CAPTURE_DIAGONAL_GIMBAL = {
  MIN: 45, // degrees
  MAX: 80, // degrees
  STEP: 1, // degrees
  DEFAULT: 60, // degrees
} as const;

/**
 * Map capture perimeter gimbal pitch constraints
 */
export const MAP_CAPTURE_PERIMETER_GIMBAL = {
  MIN: 10, // degrees
  MAX: 80, // degrees
  STEP: 1, // degrees
  DEFAULT: 60, // degrees
} as const;

/**
 * Map capture overlap constraints (front overlap percentage)
 */
export const MAP_CAPTURE_OVERLAP = {
  MIN: 1, // percent
  MAX: 95, // percent
  STEP: 1,
} as const;

/**
 * Map capture sidelap constraints (side overlap percentage)
 */
export const MAP_CAPTURE_SIDELAP = {
  MIN: 1, // percent
  MAX: 95, // percent
  STEP: 1,
} as const;

/**
 * Map capture flight height constraints
 */
export const MAP_CAPTURE_HEIGHT = {
  MIN: 1, // meters
  MAX: 122, // meters (~400 feet)
  STEP: feetToMeters(1), // ~0.3048m (1 foot)
} as const;

/**
 * Map capture maximum speed constraints
 */
export const MAP_CAPTURE_SPEED = {
  MIN: 0.5, // m/s
  MAX_BASE: 10, // m/s (S2/X2)
  MAX_X10: 16, // m/s (X10)
  STEP: 0.1, // m/s
} as const;

/**
 * Map capture scan plane offset constraints
 */
export const MAP_CAPTURE_SCAN_PLANE_OFFSET = {
  MIN: -100, // meters
  MAX: 100, // meters
  STEP: feetToMeters(1), // ~0.3048m (1 foot)
} as const;

/**
 * Map capture flight direction constraints
 */
export const MAP_CAPTURE_FLIGHT_DIRECTION = {
  MIN: 0, // degrees
  MAX: 360, // degrees
  STEP: 1, // degrees
} as const;

/**
 * Map capture diagonal leg overlap constraints
 */
export const MAP_CAPTURE_DIAGONAL_OVERLAP = {
  MIN: 1, // percent
  MAX: 95, // percent
  DEFAULT: 85, // percent
  STEP: 1,
} as const;

/**
 * Map capture perimeter leg overlap constraints
 */
export const MAP_CAPTURE_PERIMETER_OVERLAP = {
  MIN: 1, // percent
  MAX: 95, // percent
  STEP: 1,
} as const;

// ============================================================================
// Vehicle speed limits
// ============================================================================

/**
 * Vehicle speed limits by mode
 *
 * @remarks
 * These are the physical/firmware speed limits of the vehicle in different modes.
 * Mission editor constraints may be lower than these.
 */
export const VEHICLE_SPEED_LIMITS = {
  MAX_VISUAL_NAVIGATION: 16.0, // m/s - max speed with VIO
  MAX_TRANSLATIONAL: 20.0, // m/s - vehicle hardware limit
  MAX_NIGHTSENSE: 8.0, // m/s
  MAX_TWILIGHT_MODE: 14.0, // m/s
} as const;

/**
 * Vertical speed constraints
 */
export const VERTICAL_SPEED = {
  MAX_DESCENT: 4.0, // m/s
  MAX_RAPID_DESCENT: 20.0, // m/s
  MAX_ASCENT: 10.0, // m/s
} as const;

/**
 * Gimbal pitch constraints in radians
 *
 * @remarks
 * Used for teleop/pilot control. For mission editor gimbal pitch in degrees,
 * see WAYPOINT_GIMBAL_PITCH.
 */
export const GIMBAL_PITCH_RADIANS = {
  MIN: -Math.PI / 2, // -90 degrees
  MAX: Math.PI / 2, // 90 degrees
} as const;

// ============================================================================
// Height and distance constraints
// ============================================================================

/**
 * Return height constraints
 */
export const RETURN_HEIGHT = {
  MAX: 200, // meters
} as const;

/**
 * Terrain following minimum height constraints
 *
 * @remarks
 * Minimum height above ground level (AGL) for terrain-following flights.
 */
export const TERRAIN_FOLLOWING_HEIGHT = {
  MIN_AGL_FEET: 100, // feet
  MIN_AGL_METERS: feetToMeters(100), // ~30.48 meters
} as const;

/**
 * Autonomous flight height and standoff constraints
 *
 * @remarks
 * Used for teleop autonomous flight settings and pathfinder boundaries.
 */
export const AUTONOMOUS_FLIGHT = {
  MAX_HEIGHT_FEET: 1000,
  MAX_HEIGHT_METERS: feetToMeters(1000), // ~304.8m
  MAX_STANDOFF_FEET: 1000,
  MAX_STANDOFF_METERS: feetToMeters(1000), // ~304.8m
  PATHFINDER_MAX_HEIGHT_FEET: 400,
  PATHFINDER_MAX_HEIGHT_METERS: feetToMeters(400), // ~121.92m
  PATHFINDER_MIN_HEIGHT_FEET: 100,
  PATHFINDER_MIN_HEIGHT_METERS: feetToMeters(100), // ~30.48m
} as const;

// ============================================================================
// Import and file constraints
// ============================================================================

/**
 * KML file import limits
 */
export const KML_IMPORT_LIMITS = {
  MAX_WAYPOINTS: 500,
  MAX_POLYGON_VERTICES: 200,
  MAX_FILE_SIZE_MB: 10,
  MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024,
} as const;

/**
 * Return to launch constraints
 *
 * @remarks
 * See mobile/shared/appcore/params.py
 */
export const RETURN_TO_LAUNCH = {
  MIN_DISTANCE: 5, // meters
} as const;

/**
 * Asset height constraints
 *
 * @remarks
 * Minimum height threshold to reject terrain model errors in asset generation.
 */
export const ASSET_HEIGHT = {
  MIN: 1.0, // meters
} as const;

// ============================================================================
// Vehicle detection helpers
// ============================================================================

/** X10 (internal code R47, public name X10) */
export function isX10(vehicleConfig?: VehicleConfiguration): boolean {
  return vehicleConfig?.vehicleType === "R47" || vehicleConfig?.vehicleType === "X10";
}

/** X2 (internal code E1) */
export function isX2(vehicleConfig?: VehicleConfiguration): boolean {
  return vehicleConfig?.vehicleType === "E1";
}

/** S2 (internal code R3) */
export function isS2(vehicleConfig?: VehicleConfiguration): boolean {
  return vehicleConfig?.vehicleType === "R3";
}

/** Has VT300-Z (Defender) gimbal */
export function hasVT300Z(vehicleConfig?: VehicleConfiguration): boolean {
  return vehicleConfig?.gimbalType === "DEFENDER";
}

/** Has VT300-L (Inspector) gimbal */
export function hasVT300L(vehicleConfig?: VehicleConfiguration): boolean {
  return vehicleConfig?.gimbalType === "INSPECTOR";
}

/** Has V100-L (Inspector Lite) gimbal */
export function hasV100L(vehicleConfig?: VehicleConfiguration): boolean {
  return vehicleConfig?.gimbalType === "INSPECTOR_LITE";
}

/** Vehicle config is unknown or missing */
export function isUnknown(vehicleConfig?: VehicleConfiguration): boolean {
  return !vehicleConfig || vehicleConfig.vehicleType === "UNKNOWN";
}

/** Has any thermal-capable gimbal */
export function hasThermal(vehicleConfig?: VehicleConfiguration): boolean {
  return (
    hasVT300L(vehicleConfig) ||
    hasVT300Z(vehicleConfig) ||
    isX2(vehicleConfig) ||
    isUnknown(vehicleConfig)
  );
}

/** Has RJPEG-capable thermal gimbal */
export function hasRjpeg(vehicleConfig?: VehicleConfiguration): boolean {
  return hasVT300L(vehicleConfig) || hasVT300Z(vehicleConfig);
}

/** Human-readable vehicle+gimbal name */
export function humanizeVehicleConfiguration(vehicleConfig?: VehicleConfiguration): string {
  if (!vehicleConfig) return "No Device Selected";
  const parts: string[] = [];
  switch (vehicleConfig.vehicleType) {
    case "R47":
      parts.push("X10");
      break;
    case "E1":
      parts.push("X2");
      break;
    case "R3":
      parts.push("S2/S2+");
      break;
  }
  switch (vehicleConfig.gimbalType) {
    case "INSPECTOR":
      parts.push("VT300-L");
      break;
    case "INSPECTOR_LITE":
      parts.push("VT100-L");
      break;
    case "DEFENDER":
      parts.push("VT300-Z");
      break;
  }
  return parts.join(" ");
}

// ============================================================================
// Speed constraints
// ============================================================================

/**
 * Get waypoint speed constraints based on vehicle and flight mode
 */
export function getWaypointSpeedConstraints(
  flightMode: FlightMode = "nominal",
  vehicleConfig?: VehicleConfiguration
): { MIN: number; MAX: number; DEFAULT: number; DEFAULT_INDOOR: number } {
  const isR47OrX10 =
    !vehicleConfig || vehicleConfig.vehicleType === "R47" || vehicleConfig.vehicleType === "X10";

  const maxSpeedObject = isR47OrX10 ? WAYPOINT_SPEED.MAX.R47_X10 : WAYPOINT_SPEED.MAX.S2_X2;

  const maxSpeed = maxSpeedObject[flightMode];

  return {
    MIN: WAYPOINT_SPEED.MIN,
    MAX: maxSpeed,
    DEFAULT: WAYPOINT_SPEED.DEFAULT,
    DEFAULT_INDOOR: WAYPOINT_SPEED.DEFAULT_INDOOR,
  };
}

/**
 * Get return speed constraints based on vehicle and flight mode
 */
export function getReturnSpeedConstraints(
  flightMode: FlightMode = "nominal",
  vehicleConfig?: VehicleConfiguration
): { MIN: number; MAX: number; DEFAULT: number } {
  const isR47OrX10 = vehicleConfig?.vehicleType === "R47" || vehicleConfig?.vehicleType === "X10";

  const maxSpeedObject = isR47OrX10 ? RETURN_SPEED.MAX.R47_X10 : RETURN_SPEED.MAX.S2_X2;

  const maxSpeed = maxSpeedObject[flightMode];
  const defaultSpeed = isR47OrX10 ? RETURN_SPEED.DEFAULT_R47 : RETURN_SPEED.DEFAULT;

  return {
    MIN: RETURN_SPEED.MIN,
    MAX: maxSpeed,
    DEFAULT: defaultSpeed,
  };
}

/**
 * Additional time in seconds to add to scan plan time estimate for mission time.
 * Accounts for takeoff, transit to scan area, return, and landing.
 */
export const BUFFER_TIME_FOR_MISSION_SEC = 150;
